import { Injectable, inject, signal, WritableSignal } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import * as L from 'leaflet';
import {
  AirspaceElement,
  FilterLayer,
  WebrqaGeoJSONOptions,
  AupGeoJSONOptions,
  MapLayerTypeEnum
} from '../../model/map.model';
import { setHiglightStyle, setTransparentStyle } from '../layersStyle';
import { Style, StyleType } from '../../model/layers.model';
import { getAltitudeIndex } from '../../model/altitudes.model';

@Injectable({
  providedIn: 'root'
})
export class MapLayerService {
  private cookieService = inject(CookieService);
  private isUpdating = false;

  private groupedLayersSignal: WritableSignal<{ [key: string]: L.LayerGroup }> = signal({});
  private filterLayerTypesSignal: WritableSignal<FilterLayer[]> = signal([]);
  private disabledAreasSignal: WritableSignal<string[]> = signal([]);
  private showFiltersSignal: WritableSignal<boolean> = signal(false);

  readonly groupedLayers = this.groupedLayersSignal.asReadonly();
  readonly filterLayerTypes = this.filterLayerTypesSignal.asReadonly();
  readonly disabledAreas = this.disabledAreasSignal.asReadonly();
  readonly showFilters = this.showFiltersSignal.asReadonly();

  setGroupedLayers(layers: { [key: string]: L.LayerGroup }): void {
    if (this.isUpdating) {
      console.warn('MapLayerService: setGroupedLayers called while updating, skipping');
      return;
    }
    this.groupedLayersSignal.set(layers);
  }

  setFilterLayerTypes(filterTypes: FilterLayer[]): void {
    if (this.isUpdating) {
      console.warn('MapLayerService: setFilterLayerTypes called while updating, skipping');
      return;
    }
    this.isUpdating = true;
    this.filterLayerTypesSignal.set([...filterTypes]);
    this.isUpdating = false;
  }

  updateFilterLayerType(type: string, checked: boolean): void {
    if (this.isUpdating) {
      console.warn('MapLayerService: updateFilterLayerType called while updating, skipping');
      return;
    }
    this.isUpdating = true;
    this.filterLayerTypesSignal.update((current) =>
      current.map((filter) => (filter.type === type ? { ...filter, checked } : filter))
    );
    this.isUpdating = false;
  }

  toggleFilters(): void {
    this.showFiltersSignal.update((current) => !current);
  }

  clearAllLayers(map: L.Map): void {
    if (!map || typeof map.eachLayer !== 'function') {
      console.warn('MapLayerService: clearAllLayers - invalid map');
      return;
    }
    map.eachLayer((layer) => {
      if (layer instanceof L.FeatureGroup) {
        map.removeLayer(layer);
      }
    });
  }

  setLayerStyles(layer: L.GeoJSON, styles: Style, styleType: StyleType): void {
    if (!layer || !styles) return;

    switch (styleType) {
      case StyleType.ACTIVATED:
        layer.setStyle(styles);
        break;
      case StyleType.HIGHLIGHT:
        layer.setStyle(setHiglightStyle(styles));
        break;
      case StyleType.TRANSPARENT:
        layer.setStyle(setTransparentStyle(styles));
    }
  }

  resetLayerStyles(): void {
    const grouped = this.groupedLayersSignal();
    Object.values(grouped).forEach((group) => {
      if (group && typeof group.eachLayer === 'function') {
        group.eachLayer((l) => {
          if (l instanceof L.GeoJSON) {
            const styles = (l.options as WebrqaGeoJSONOptions | AupGeoJSONOptions).styles;
            if (styles) {
              this.setLayerStyles(l, styles, StyleType.ACTIVATED);
            }
          }
        });
      }
    });
  }

  bringToFrontLayers(types: MapLayerTypeEnum[]): void {
    const grouped = this.groupedLayersSignal();
    types.forEach((type) => {
      const group = grouped[type];
      if (group && typeof group.eachLayer === 'function') {
        group.eachLayer((layer) => {
          if (layer instanceof L.GeoJSON) {
            layer.bringToFront();
          }
        });
      }
    });
  }

  toggleLayerGroupVisibility(visible: boolean, groupId: string, map: L.Map): void {
    if (this.isUpdating) {
      console.warn('MapLayerService: toggleLayerGroupVisibility called while updating, skipping');
      return;
    }
    if (!map) {
      console.warn('MapLayerService: toggleLayerGroupVisibility - invalid map');
      return;
    }

    const grouped = this.groupedLayersSignal();
    const group = grouped[groupId];

    // Update filter type without triggering effects
    this.isUpdating = true;
    this.filterLayerTypesSignal.update((current) =>
      current.map((filter) => (filter.type === groupId ? { ...filter, checked: visible } : filter))
    );
    this.isUpdating = false;

    if (visible && group) {
      group.addTo(map);
    } else if (group) {
      group.remove();
    }
  }

  filterLayersByHeightRange(minHeight: string, maxHeight: string, map: L.Map): void {
    if (!map) {
      console.warn('MapLayerService: filterLayersByHeightRange - invalid map');
      return;
    }

    const minHeightIndex = getAltitudeIndex(minHeight);
    const maxHeightIndex = getAltitudeIndex(maxHeight);
    const grouped = this.groupedLayersSignal();
    const filterTypes = this.filterLayerTypesSignal();

    filterTypes
      .filter((layerType) => layerType.checked)
      .forEach((layerType) => {
        const groupKey = layerType.type;
        const layerGroup = grouped[groupKey];

        if (!layerGroup || typeof layerGroup.eachLayer !== 'function') return;

        layerGroup.eachLayer((layer: L.Layer) => {
          const geoJsonLayer = layer as L.GeoJSON;
          const options = geoJsonLayer.options as WebrqaGeoJSONOptions;

          if (!options.availabilities || options.availabilities.length === 0) {
            return;
          }

          const meetsHeightCriteria = options.availabilities.some((availability) => {
            const lowerAltitudeIndex = getAltitudeIndex(availability.lowerAltitude);
            const upperAltitudeIndex = getAltitudeIndex(availability.upperAltitude);

            return (
              (lowerAltitudeIndex >= minHeightIndex && lowerAltitudeIndex <= maxHeightIndex) ||
              (upperAltitudeIndex >= minHeightIndex && upperAltitudeIndex <= maxHeightIndex) ||
              (lowerAltitudeIndex <= minHeightIndex && upperAltitudeIndex >= maxHeightIndex)
            );
          });

          if (meetsHeightCriteria) {
            if (!map.hasLayer(geoJsonLayer)) {
              geoJsonLayer.addTo(map);
            }
          } else {
            if (map.hasLayer(geoJsonLayer)) {
              map.removeLayer(geoJsonLayer);
            }
          }
        });
      });
  }

  setDisabledAreas(): void {
    const filterTypes = this.filterLayerTypesSignal();
    const disabled = filterTypes.filter((el) => !el.checked).map((el) => el.type);
    this.disabledAreasSignal.set(disabled);

    try {
      this.cookieService.set(
        'inactiveAreas',
        JSON.stringify(Array.from(new Set(disabled))),
        365,
        '/',
        '',
        true,
        'Strict'
      );
    } catch (error) {
      console.warn('Failed to set cookie:', error);
    }
  }

  getDisabledAreas(): void {
    try {
      const disabled = this.cookieService.get('inactiveAreas')
        ? Array.from(new Set(JSON.parse(this.cookieService.get('inactiveAreas')) as string[]))
        : [];
      this.disabledAreasSignal.set(disabled);
    } catch (error) {
      console.warn('Failed to get disabled areas from cookie:', error);
      this.disabledAreasSignal.set([]);
    }
  }

  moveCoordinatesDiv(showFilters: boolean): void {
    const container = document.getElementById('coordinatesContainer');
    if (container) {
      if (showFilters) {
        container.style.right = `6.5rem`;
        container.style.bottom = `5.3rem`;
      } else {
        container.style.right = `0rem`;
        container.style.bottom = `1rem`;
      }
    }
  }

  findIntersectingLayers(
    latlng: L.LatLng,
    map: L.Map,
    isPointInLayerFn: (latlng: L.LatLng, layer: L.GeoJSON) => boolean
  ): AirspaceElement[] {
    if (!map || typeof map.eachLayer !== 'function') {
      console.warn('MapLayerService: findIntersectingLayers - invalid map');
      return [];
    }

    const selectedLayers: AirspaceElement[] = [];

    map.eachLayer((layer) => {
      if (map.hasLayer(layer)) {
        if (isPointInLayerFn(latlng, layer as L.GeoJSON)) {
          const layerData = layer.options as WebrqaGeoJSONOptions;
          const isExistDesignator = selectedLayers.find(
            (el: AirspaceElement) => el.designator === layerData.designator
          );
          if (!isExistDesignator) {
            selectedLayers.push(layerData as AirspaceElement);
          }
        }
      }
    });

    return selectedLayers;
  }

  forEachLayer(callback: (layer: L.Layer, groupKey: string) => void): void {
    const grouped = this.groupedLayersSignal();
    Object.keys(grouped).forEach((key) => {
      const layerGroup = grouped[key];
      if (layerGroup && typeof layerGroup.eachLayer === 'function') {
        layerGroup.eachLayer((layer) => {
          callback(layer, key);
        });
      }
    });
  }

  convertGeometry(
    geometry: { coordinates: unknown; type: string }[]
  ): { coordinates: unknown; type: string } | { type: string; coordinates: unknown[] } {
    if (geometry.length > 1) {
      return {
        type: 'MultiPolygon',
        coordinates: geometry.map((geo) => geo.coordinates)
      };
    } else {
      return geometry[0];
    }
  }
}
